import React from 'react';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import PasswordConfirmModal from './PasswordConfirmModal';
import TwoFactorCard from './security/TwoFactorCard';
import StrictModeCard from './security/StrictModeCard';
import DeviceSessionsCard from './security/DeviceSessionsCard';
import DataPrivacyCard from './security/DataPrivacyCard';
import { useSecuritySettings } from './hooks/useSecuritySettings';

const SecuritySettings = () => {
  const {
    loading, activeSessions, showDeviceSessions, setShowDeviceSessions,
    twoFactorStatus, showQRCode, setShowQRCode, qrCodeUrl, totpSecret, otpauthUri,
    verificationCode, setVerificationCode,
    error, setError, success,
    actionLoading, strictMode,
    showPasswordModal, setShowPasswordModal,
    setPendingAction, passwordModalConfig,
    handleSetup2FA, handleVerify2FA, handleDisable2FA,
    handlePasswordConfirm, handleToggleStrictMode, handleLogoutAllDevices,
  } = useSecuritySettings();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PasswordConfirmModal
        isOpen={showPasswordModal}
        onClose={() => { setShowPasswordModal(false); setPendingAction(null); }}
        onConfirm={handlePasswordConfirm}
        actionTitle={passwordModalConfig.title}
        actionDescription={passwordModalConfig.description}
        isLoading={actionLoading}
      />
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription className="text-emerald-800">{success}</AlertDescription>
        </Alert>
      )}
      {/* Session 262: TwoFactorCard promoted to the top so users landing on
          the Security sub-tab from the "Set up 2FA in Security" button on
          the Overview banner see the 2FA card as the first thing on screen,
          with no scrolling needed. */}
      <TwoFactorCard
        twoFactorStatus={twoFactorStatus} actionLoading={actionLoading}
        onSetup2FA={handleSetup2FA} onVerify2FA={handleVerify2FA} onDisable2FA={handleDisable2FA}
        showQRCode={showQRCode} qrCodeUrl={qrCodeUrl} totpSecret={totpSecret} otpauthUri={otpauthUri}
        verificationCode={verificationCode} setVerificationCode={setVerificationCode}
        setShowQRCode={setShowQRCode} setError={setError}
      />
      <DataPrivacyCard />
      <StrictModeCard strictMode={strictMode} onToggle={handleToggleStrictMode} actionLoading={actionLoading} />
      <DeviceSessionsCard
        showDeviceSessions={showDeviceSessions} setShowDeviceSessions={setShowDeviceSessions}
        activeSessions={activeSessions} onLogoutAll={handleLogoutAllDevices} actionLoading={actionLoading}
      />
    </div>
  );
};

export default SecuritySettings;
